// ============================================================
// InventoryTab.jsx  —  Noble Enterprises (tape module)
//
// WHAT'S ON THIS SCREEN
//   • Every physical jumbo roll in stock
//   • How much of each is left, as a bar and a number
//   • Filters by state, tape type, vendor, roll number
//   • Close a finished roll
//
// Rolls are created by Inward. Nothing here adds one — this is
// a view of stock, plus the one action that ends a roll's life.
//
// HOW THIS FILE IS ORGANISED
//   A. Shared UI bits
//   B. StateBadge + MeterBar
//   C. CloseRollModal
//   D. RollDetailRow
//   E. InventoryTab
// ============================================================

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { X, Check, Search, Archive, RotateCcw, ChevronDown as Chevron } from 'lucide-react';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const C = {
  brand:      '#f39c12',
  text:       '#1a2332',
  textSoft:   '#64748b',
  textMuted:  '#94a3b8',
  border:     '#e2e8f0',
  borderSoft: '#f1f5f9',
  card:       '#ffffff',
  rowAlt:     '#fafbfc',
  danger:     '#dc2626',
  dangerBg:   '#fee2e2',
  success:    '#059669',
  successBg:  '#d1fae5',
  warning:    '#d97706',
  warningBg:  '#fef3c7',
  info:       '#0284c7',
  infoBg:     '#e0f2fe',
};

const money  = (n) => `Rs ${Number(n || 0).toLocaleString('en-PK', { maximumFractionDigits: 0 })}`;
const meters = (n) => `${Number(n || 0).toLocaleString('en-PK', { maximumFractionDigits: 0 })} m`;
const fmtDate = (d) => new Date(d).toLocaleDateString('en-GB');


// ============================================================
// A. SHARED UI BITS
// ============================================================

const inputStyle = {
  width: '100%', padding: '10px 13px',
  border: `1px solid ${C.border}`, borderRadius: '8px',
  fontSize: '14px', color: C.text, boxSizing: 'border-box',
  fontFamily: 'inherit', background: '#ffffff',
};

const Field = ({ label, children }) => (
  <div>
    <label style={{
      display: 'block', fontSize: '12px', fontWeight: '600',
      color: C.textSoft, marginBottom: '6px',
    }}>
      {label}
    </label>
    {children}
  </div>
);

const TextInput = (props) => <input style={inputStyle} {...props} />;

const Btn = ({ variant = 'primary', children, ...props }) => {
  const styles = {
    primary: { background: C.brand,   color: '#ffffff',  border: 'none' },
    ghost:   { background: '#ffffff', color: C.textSoft, border: `1px solid ${C.border}` },
  };
  return (
    <button
      style={{
        ...styles[variant],
        padding: '10px 18px', borderRadius: '8px', fontSize: '14px',
        fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit',
        display: 'inline-flex', alignItems: 'center', gap: '7px',
      }}
      {...props}
    >
      {children}
    </button>
  );
};

const IconBtn = ({ danger, title, children, ...props }) => (
  <button
    title={title}
    style={{
      width: '32px', height: '32px', borderRadius: '7px',
      border: `1px solid ${C.border}`, background: '#ffffff',
      color: danger ? C.danger : C.textSoft, cursor: 'pointer',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    }}
    {...props}
  >
    {children}
  </button>
);

const Banner = ({ type, message, onClose }) => {
  if (!message) return null;
  const isError = type === 'error';
  return (
    <div style={{
      background: isError ? C.dangerBg : C.successBg,
      color:      isError ? C.danger   : C.success,
      padding: '11px 16px', borderRadius: '8px', marginBottom: '16px',
      fontSize: '14px', display: 'flex', alignItems: 'center', gap: '10px',
    }}>
      <span style={{ flex: 1 }}>{message}</span>
      <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', display: 'flex' }}>
        <X size={16} />
      </button>
    </div>
  );
};

const Modal = ({ title, subtitle, onClose, width = '520px', children }) => (
  <div
    onClick={onClose}
    style={{
      position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: '20px',
    }}
  >
    <div
      onClick={e => e.stopPropagation()}
      style={{
        background: '#ffffff', borderRadius: '14px', width, maxWidth: '100%',
        maxHeight: '88vh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
      }}
    >
      <div style={{
        padding: '20px 24px', borderBottom: `1px solid ${C.borderSoft}`,
        display: 'flex', alignItems: 'flex-start', gap: '14px',
      }}>
        <div style={{ flex: 1 }}>
          <h3 style={{ margin: 0, fontSize: '17px', fontWeight: '700', color: C.text }}>{title}</h3>
          {subtitle && <p style={{ margin: '3px 0 0', fontSize: '13px', color: C.textSoft }}>{subtitle}</p>}
        </div>
        <button
          onClick={onClose}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted, display: 'flex', padding: '2px' }}
        >
          <X size={20} />
        </button>
      </div>
      <div style={{ padding: '22px 24px', overflowY: 'auto' }}>{children}</div>
    </div>
  </div>
);

const thStyle = {
  textAlign: 'left', padding: '11px 18px', fontSize: '12px',
  fontWeight: '600', color: C.textSoft, background: C.rowAlt,
  textTransform: 'uppercase', letterSpacing: '0.3px',
};
const tdStyle = {
  padding: '13px 18px', fontSize: '14px', color: C.text,
  borderTop: `1px solid ${C.borderSoft}`,
};


// ============================================================
// B. STATE BADGE + METER BAR
// ============================================================

// A roll is in one of three states. Only 'closed' is stored —
// the other two depend on whether the roll can still give a
// full core, which is worked out from its meters.
const rollState = (roll) => {
  if (roll.status === 'closed') return 'closed';
  return roll.is_nearly_done ? 'nearly_done' : 'active';
};

const STATE_STYLE = {
  active:      { label: 'Active',      bg: C.successBg,  fg: C.success },
  nearly_done: { label: 'Nearly done', bg: C.warningBg,  fg: C.warning },
  closed:      { label: 'Closed',      bg: C.borderSoft, fg: C.textMuted },
};

const StateBadge = ({ roll }) => {
  const s = STATE_STYLE[rollState(roll)];
  return (
    <span style={{
      fontSize: '12px', fontWeight: '600', padding: '3px 11px',
      borderRadius: '11px', background: s.bg, color: s.fg,
      whiteSpace: 'nowrap',
    }}>
      {s.label}
    </span>
  );
};

// Bar shows what's LEFT, not what's used — longer is better,
// which reads the right way at a glance.
const MeterBar = ({ remaining, total, state }) => {
  const pct = total > 0 ? Math.max(0, Math.min(100, (remaining / total) * 100)) : 0;
  const colour = state === 'closed' ? C.textMuted
               : state === 'nearly_done' ? C.warning
               : C.success;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
      <div style={{
        flex: 1, minWidth: '60px', height: '6px', borderRadius: '3px',
        background: C.borderSoft, overflow: 'hidden',
      }}>
        <div style={{ width: `${pct}%`, height: '100%', background: colour }} />
      </div>
      <span style={{
        fontSize: '12px', color: C.textMuted, minWidth: '34px', textAlign: 'right',
      }}>
        {Math.round(pct)}%
      </span>
    </div>
  );
};


// ============================================================
// C. CLOSE ROLL MODAL
//
// The scrap field is what makes closing honest. It reconciles
// what the books say is left against what's physically on the
// floor, and records the gap instead of letting it disappear.
// ============================================================

const CloseRollModal = ({ roll, onClose, onDone, notify }) => {
  const [scrapKg, setScrapKg] = useState('');
  const [saving, setSaving]   = useState(false);

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const bookRemaining = Number(roll.remaining_meters);

  const save = async () => {
    if (scrapKg !== '' && Number(scrapKg) < 0) {
      return notify('error', 'Scrap weight cannot be negative');
    }
    setSaving(true);
    try {
      await axios.put(`${API}/tape/jumbo-rolls/${roll.id}/close`,
        { closing_scrap_kg: scrapKg }, { headers });
      notify('success', `Roll ${roll.vendor_roll_number} closed`);
      onDone();
      onClose();
    } catch (err) {
      notify('error', err.response?.data?.message || 'Could not close this roll');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title="Close roll"
      subtitle={`${roll.vendor_roll_number} · ${roll.jumbo_name}`}
      onClose={onClose}
    >
      <div style={{
        background: C.rowAlt, borderRadius: '8px', padding: '14px 16px',
        marginBottom: '18px', display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: '12px',
      }}>
        {[
          ['Received', meters(roll.total_meters)],
          ['Consumed', meters(roll.consumed_meters)],
          ['Book remaining', meters(bookRemaining)],
        ].map(([label, value]) => (
          <div key={label}>
            <div style={{ fontSize: '11px', color: C.textMuted, fontWeight: '600', textTransform: 'uppercase' }}>
              {label}
            </div>
            <div style={{ fontSize: '15px', fontWeight: '700', color: C.text, marginTop: '3px' }}>
              {value}
            </div>
          </div>
        ))}
      </div>

      <Field label="Actual scrap left (kg)">
        <TextInput
          type="number" step="0.01" min="0"
          value={scrapKg}
          onChange={e => setScrapKg(e.target.value)}
          placeholder="0"
          autoFocus
        />
      </Field>

      <p style={{ fontSize: '12px', color: C.textMuted, margin: '8px 0 0', lineHeight: 1.5 }}>
        Weigh what's physically left of this roll. The books still show
        {' '}<strong style={{ color: C.text }}>{meters(bookRemaining)}</strong>{' '}
        unaccounted for — closing records that as loss rather than leaving
        it open forever.
      </p>

      <div style={{
        marginTop: '18px', padding: '11px 14px', borderRadius: '8px',
        background: C.warningBg, color: '#78350f', fontSize: '13px', lineHeight: 1.5,
      }}>
        Once closed, this roll's yield variance is final and it disappears
        from production. Only the owner can reopen it.
      </div>

      <div style={{ display: 'flex', gap: '10px', marginTop: '22px' }}>
        <Btn onClick={save} disabled={saving}>
          <Check size={16} /> {saving ? 'Closing…' : 'Close roll'}
        </Btn>
        <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
      </div>
    </Modal>
  );
};


// ============================================================
// D. DETAIL ROW
// ============================================================

const RollDetailRow = ({ roll, colSpan }) => {
  const expected = Number(roll.expected_cores);
  const spec     = Number(roll.spec_meters);
  const actual   = Number(roll.total_meters);
  const isShort  = actual < spec;

  const box = (label, value, colour = C.text) => (
    <div key={label} style={{
      background: '#ffffff', border: `1px solid ${C.border}`,
      borderRadius: '8px', padding: '12px 14px',
    }}>
      <div style={{ fontSize: '11px', color: C.textMuted, fontWeight: '600', textTransform: 'uppercase' }}>
        {label}
      </div>
      <div style={{ fontSize: '15px', fontWeight: '700', color: colour, marginTop: '3px' }}>
        {value}
      </div>
    </div>
  );

  return (
    <tr>
      <td colSpan={colSpan} style={{
        padding: '18px 22px', background: C.rowAlt,
        borderTop: `1px solid ${C.borderSoft}`,
      }}>
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
          gap: '12px', marginBottom: '14px',
        }}>
          {box('Tape type', roll.tape_type_name)}
          {box('Category', roll.category_name)}
          {box('Quality', roll.quality_name)}
          {box('Width', `${roll.width_inches}"`)}
          {box('Core length', meters(roll.core_length_meters))}
          {box('Expected cores', expected)}
        </div>

        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: '12px',
        }}>
          {box('Vendor', roll.vendor_name)}
          {box('Purchase order', roll.po_number)}
          {box('Bill number', roll.bill_number)}
          {box('Received', fmtDate(roll.received_date))}
          {box('Roll cost', money(roll.rate))}
          {roll.status === 'closed' && box('Closing scrap', `${Number(roll.closing_scrap_kg || 0)} kg`)}
        </div>

        {/* A short roll changes what it can be expected to yield.
            Worth surfacing here so nobody blames the operator. */}
        {isShort && (
          <div style={{
            marginTop: '14px', padding: '11px 14px', borderRadius: '8px',
            background: C.warningBg, color: '#78350f', fontSize: '13px',
          }}>
            This roll arrived at {meters(actual)} against a spec of {meters(spec)}.
            Expected cores is calculated from what actually arrived.
          </div>
        )}
      </td>
    </tr>
  );
};


// ============================================================
// E. MAIN COMPONENT
// ============================================================

const InventoryTab = () => {
  const [rolls, setRolls]     = useState([]);
  const [summary, setSummary] = useState(null);
  const [tapeTypes, setTapeTypes] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [closingRoll, setClosingRoll] = useState(null);
  const [banner, setBanner]   = useState({ type: '', message: '' });

  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter]     = useState('');
  const [vendorFilter, setVendorFilter] = useState('');
  const [search, setSearch]             = useState('');

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
  const role = JSON.parse(localStorage.getItem('user') || '{}')?.role;
  const canReopen = role === 'businessman' || role === 'admin';

  const notify = (type, message) => {
    setBanner({ type, message });
    if (type === 'success') setTimeout(() => setBanner({ type: '', message: '' }), 3000);
  };

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (typeFilter)   params.append('tape_type_id', typeFilter);
      if (vendorFilter) params.append('vendor_id', vendorFilter);
      if (search.trim()) params.append('search', search.trim());

      const [r, s, t, v] = await Promise.all([
        axios.get(`${API}/tape/jumbo-rolls?${params.toString()}`, { headers }),
        axios.get(`${API}/tape/jumbo-rolls/summary`, { headers }),
        axios.get(`${API}/tape/settings/tape-types`, { headers }),
        axios.get(`${API}/tape/vendors/active`, { headers }),
      ]);
      setRolls(r.data.data || []);
      setSummary(s.data.data || null);
      setTapeTypes(t.data.data || []);
      setVendors(v.data.data || []);
    } catch {
      notify('error', 'Could not load inventory. Is the backend running?');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, typeFilter, vendorFilter, search]); // eslint-disable-line

  // Typing in search shouldn't fire a request per keystroke
  useEffect(() => {
    const timer = setTimeout(() => { load(); }, 300);
    return () => clearTimeout(timer);
  }, [load]);

  const reopen = async (roll) => {
    if (!window.confirm(
      `Reopen roll ${roll.vendor_roll_number}? Its yield variance will start moving again.`
    )) return;

    try {
      await axios.put(`${API}/tape/jumbo-rolls/${roll.id}/reopen`, {}, { headers });
      notify('success', 'Roll reopened');
      load();
    } catch (err) {
      notify('error', err.response?.data?.message || 'Could not reopen');
    }
  };

  return (
    <div>
      <Banner
        type={banner.type}
        message={banner.message}
        onClose={() => setBanner({ type: '', message: '' })}
      />

      {/* Summary */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        gap: '14px', marginBottom: '20px',
      }}>
        {[
          ['Active rolls',  summary?.active_rolls ?? '—', C.text],
          ['Nearly done',   summary?.nearly_done_rolls ?? '—',
            Number(summary?.nearly_done_rolls) > 0 ? C.warning : C.textMuted],
          ['Meters in stock', summary ? meters(summary.meters_in_stock) : '—', C.text],
          ['Stock value',   summary ? money(summary.stock_value) : '—', C.text],
          ['Closed rolls',  summary?.closed_rolls ?? '—', C.textMuted],
        ].map(([label, value, colour]) => (
          <div key={label} style={{
            background: C.card, border: `1px solid ${C.border}`,
            borderRadius: '10px', padding: '16px 18px',
          }}>
            <div style={{ fontSize: '11px', color: C.textMuted, fontWeight: '600', textTransform: 'uppercase' }}>
              {label}
            </div>
            <div style={{ fontSize: '21px', fontWeight: '700', color: colour, marginTop: '4px' }}>
              {value}
            </div>
          </div>
        ))}
      </div>

      <div style={{
        background: C.card, border: `1px solid ${C.border}`,
        borderRadius: '12px', overflow: 'hidden',
      }}>
        {/* Header + filters */}
        <div style={{
          padding: '18px 22px', borderBottom: `1px solid ${C.borderSoft}`,
        }}>
          <div style={{ marginBottom: '14px' }}>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: C.text }}>
              Jumbo roll stock
            </h3>
            <p style={{ margin: '3px 0 0', fontSize: '13px', color: C.textSoft }}>
              Click a roll to see its specs and where it came from. Oldest first.
            </p>
          </div>

          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <div style={{ position: 'relative', flex: '1 1 200px', minWidth: '180px' }}>
              <Search
                size={15}
                style={{
                  position: 'absolute', left: '12px', top: '50%',
                  transform: 'translateY(-50%)', color: C.textMuted,
                }}
              />
              <input
                style={{ ...inputStyle, paddingLeft: '34px' }}
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search roll number…"
              />
            </div>

            <select
              style={{ ...inputStyle, width: 'auto', minWidth: '140px' }}
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
            >
              <option value="">All States</option>
              <option value="active">Active</option>
              <option value="nearly_done">Nearly done</option>
              <option value="closed">Closed</option>
            </select>

            <select
              style={{ ...inputStyle, width: 'auto', minWidth: '140px' }}
              value={typeFilter}
              onChange={e => setTypeFilter(e.target.value)}
            >
              <option value="">All Types</option>
              {tapeTypes.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>

            <select
              style={{ ...inputStyle, width: 'auto', minWidth: '150px' }}
              value={vendorFilter}
              onChange={e => setVendorFilter(e.target.value)}
            >
              <option value="">All Vendors</option>
              {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
          </div>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}>Roll number</th>
                <th style={thStyle}>Jumbo</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Received</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Remaining</th>
                <th style={{ ...thStyle, minWidth: '130px' }}>Left</th>
                <th style={thStyle}>Vendor</th>
                <th style={thStyle}>State</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan="8" style={{ ...tdStyle, textAlign: 'center', color: C.textMuted, padding: '36px' }}>
                    Loading…
                  </td>
                </tr>
              )}

              {!loading && rolls.map(roll => {
                const isOpen = expandedId === roll.id;
                const state  = rollState(roll);

                return (
                  <React.Fragment key={roll.id}>
                    <tr
                      onClick={() => setExpandedId(isOpen ? null : roll.id)}
                      style={{
                        cursor: 'pointer',
                        background: isOpen ? C.rowAlt : 'transparent',
                        opacity: state === 'closed' ? 0.65 : 1,
                      }}
                    >
                      <td style={{ ...tdStyle, fontWeight: '700' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '7px' }}>
                          <Chevron
                            size={15}
                            style={{
                              color: C.textMuted,
                              transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                              transition: 'transform 0.15s',
                            }}
                          />
                          {roll.vendor_roll_number || '—'}
                        </span>
                      </td>
                      <td style={tdStyle}>
                        <div style={{ fontSize: '13px' }}>{roll.jumbo_name}</div>
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right', color: C.textSoft }}>
                        {meters(roll.total_meters)}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right', fontWeight: '600' }}>
                        {meters(roll.remaining_meters)}
                      </td>
                      <td style={tdStyle}>
                        <MeterBar
                          remaining={Number(roll.remaining_meters)}
                          total={Number(roll.total_meters)}
                          state={state}
                        />
                      </td>
                      <td style={{ ...tdStyle, fontSize: '13px' }}>{roll.vendor_name}</td>
                      <td style={tdStyle}><StateBadge roll={roll} /></td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>
                        {/* stopPropagation, or the click also toggles the row */}
                        {roll.status === 'active' && (
                          <IconBtn
                            title="Close roll"
                            onClick={(e) => { e.stopPropagation(); setClosingRoll(roll); }}
                          >
                            <Archive size={15} />
                          </IconBtn>
                        )}
                        {roll.status === 'closed' && canReopen && (
                          <IconBtn
                            title="Reopen roll"
                            onClick={(e) => { e.stopPropagation(); reopen(roll); }}
                          >
                            <RotateCcw size={15} />
                          </IconBtn>
                        )}
                      </td>
                    </tr>

                    {isOpen && <RollDetailRow roll={roll} colSpan={8} />}
                  </React.Fragment>
                );
              })}

              {!loading && rolls.length === 0 && (
                <tr>
                  <td colSpan="8" style={{ ...tdStyle, textAlign: 'center', color: C.textMuted, padding: '40px' }}>
                    No rolls match. Receive goods against a purchase order to build stock.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {closingRoll && (
        <CloseRollModal
          roll={closingRoll}
          onClose={() => setClosingRoll(null)}
          onDone={load}
          notify={notify}
        />
      )}
    </div>
  );
};

export default InventoryTab;